import { useEffect, useMemo, useState, type JSX } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Carregando, Erro, Vazio } from '../componentes/Estados.js';
import { requisitar, type RespostaMeta } from '../lib/api.js';
import './Catalogo.css';

/**
 * Catálogo de produtos — Story 8.2 (RF-06/09, RNF-07).
 *
 * ## A ordenação é do servidor
 *
 * A lista chega ordenada por `(ordem_grupo, formato_ord_1, formato_ord_2,
 * referencia)` e é renderizada **na ordem em que chega**. Reordenar aqui seria
 * ordenar por texto — e `"100x100"` viria antes de `"60x60"`, que é exatamente
 * o defeito que o `formato_ord` existe para evitar. Além disso, com paginação
 * uma ordenação local só acertaria dentro da página.
 *
 * ## Por que paginação e não virtualização
 *
 * O catálogo real tem ~300 referências. Uma página de 50 linhas rola liso em
 * qualquer máquina e mantém o HTML pequeno; virtualizar traria complexidade
 * (altura de linha fixa, acessibilidade de tabela, rolagem restaurada) sem
 * ganho perceptível nessa ordem de grandeza. Se um tenant chegar a milhares de
 * refs, a decisão se revisa — a paginação do servidor já está pronta.
 */

interface ProdutoLista {
  id: string;
  referencia: string;
  formato: string | null;
  formatoOrd1: string;
  formatoOrd2: string;
  unidade: string;
  medidaCaixa: string;
  pesoBrutoUnidade: string;
  ipi: string;
  ativo: boolean;
  classeCode: string;
  ordemGrupo: number;
  atributosJson: Record<string, unknown> | null;
}

interface Classe {
  id: string;
  code: string;
  nome: string;
}

const POR_PAGINA = 50;

const NOME_CLASSE: Record<string, string> = {
  normal: 'Porcelanato/Cerâmica',
  villa_arte: 'Villa Arte',
  rodape: 'Rodapé',
};

const NOME_UNIDADE: Record<string, string> = {
  m2: 'm²',
  metro_linear: 'm linear',
};

/** Espera o usuário parar de digitar antes de ir ao servidor. */
function useDebounce<T>(valor: T, ms = 300): T {
  const [atrasado, setAtrasado] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => {
      setAtrasado(valor);
    }, ms);
    return () => {
      clearTimeout(t);
    };
  }, [valor, ms]);
  return atrasado;
}

function decimalBr(valor: string, casas = 2): string {
  const n = Number(valor);
  if (!Number.isFinite(n)) return valor;
  return n.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
}

export function Catalogo(): JSX.Element {
  const [busca, setBusca] = useState('');
  const [classeId, setClasseId] = useState('');
  const [apenasAtivos, setApenasAtivos] = useState(true);
  const [pagina, setPagina] = useState(0);

  const buscaAtrasada = useDebounce(busca);

  // Trocar filtro sem voltar à primeira página deixaria o usuário numa página
  // vazia sem entender por quê.
  useEffect(() => {
    setPagina(0);
  }, [buscaAtrasada, classeId, apenasAtivos]);

  const classes = useQuery({
    queryKey: ['classes'],
    queryFn: async () => (await requisitar<Classe[]>('/api/produtos/classes')).dados,
    staleTime: 5 * 60 * 1000,
  });

  const consulta = useMemo(() => {
    const p = new URLSearchParams();
    if (buscaAtrasada.trim() !== '') p.set('busca', buscaAtrasada.trim());
    if (classeId !== '') p.set('classeId', classeId);
    if (apenasAtivos) p.set('apenasAtivos', 'true');
    p.set('limite', String(POR_PAGINA));
    p.set('offset', String(pagina * POR_PAGINA));
    return p.toString();
  }, [buscaAtrasada, classeId, apenasAtivos, pagina]);

  const produtos = useQuery({
    queryKey: ['produtos', consulta],
    queryFn: async () => {
      const r = await requisitar<ProdutoLista[]>(`/api/produtos?${consulta}`);
      return { itens: r.dados, meta: r.meta };
    },
    // Mantém a lista anterior enquanto a nova carrega: sem isto, cada tecla
    // digitada faria a tabela piscar para o estado vazio.
    placeholderData: keepPreviousData,
  });

  const itens = produtos.data?.itens ?? [];
  const meta: RespostaMeta | undefined = produtos.data?.meta;
  const total = meta?.total ?? 0;
  const primeiro = total === 0 ? 0 : pagina * POR_PAGINA + 1;
  const ultimo = Math.min((pagina + 1) * POR_PAGINA, total);
  const temProxima = ultimo < total;

  return (
    <section className="catalogo">
      <header className="pagina__cabecalho">
        <div>
          <h1>Catálogo</h1>
          <p className="pagina__sub">
            Produtos ordenados por tamanho, como na planilha. Rodapés no fim.
          </p>
        </div>
      </header>

      <div className="catalogo__filtros cartao">
        <label className="campo catalogo__busca">
          <span className="apenas-leitor">Buscar produto</span>
          <input
            className="campo__entrada"
            type="search"
            value={busca}
            onChange={(e) => {
              setBusca(e.target.value);
            }}
            placeholder="Buscar por referência, formato ou linha…"
          />
        </label>

        <label className="campo">
          <span className="apenas-leitor">Filtrar por classe</span>
          <select
            className="campo__entrada"
            value={classeId}
            onChange={(e) => {
              setClasseId(e.target.value);
            }}
          >
            <option value="">Todas as classes</option>
            {(classes.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {NOME_CLASSE[c.code] ?? c.nome}
              </option>
            ))}
          </select>
        </label>

        <label className="catalogo__checkbox">
          <input
            type="checkbox"
            checked={apenasAtivos}
            onChange={(e) => {
              setApenasAtivos(e.target.checked);
            }}
          />
          Somente ativos
        </label>
      </div>

      {produtos.isPending ? (
        <div className="cartao">
          <Carregando rotulo="Carregando o catálogo…" />
        </div>
      ) : produtos.isError ? (
        <div className="cartao">
          <Erro
            erro={produtos.error}
            aoRepetir={() => {
              void produtos.refetch();
            }}
          />
        </div>
      ) : itens.length === 0 ? (
        <div className="cartao">
          <Vazio
            titulo="Nenhum produto encontrado"
            descricao={
              busca.trim() === ''
                ? 'Este tenant ainda não tem produtos no catálogo. Importe uma tabela de preço para carregá-los.'
                : `Nada corresponde a “${busca.trim()}”. Tente outra referência ou limpe os filtros.`
            }
          />
        </div>
      ) : (
        <div className="cartao catalogo__quadro">
          <div className="catalogo__rolagem">
            <table className="tabela tabela--cards">
              <caption className="apenas-leitor">
                Produtos do catálogo, ordenados por tamanho
              </caption>
              <thead>
                <tr>
                  <th scope="col">Referência</th>
                  <th scope="col">Formato</th>
                  <th scope="col">Linha</th>
                  <th scope="col">Classe</th>
                  <th scope="col">Unidade</th>
                  <th scope="col" className="num">
                    Por caixa
                  </th>
                  <th scope="col" className="num">
                    Peso/un. (kg)
                  </th>
                  <th scope="col" className="num">
                    IPI
                  </th>
                  <th scope="col">Situação</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((p) => (
                  <tr key={p.id}>
                    <td className="catalogo__ref" data-rotulo="Referência">
                      {p.referencia}
                    </td>
                    <td data-rotulo="Formato">{p.formato ?? '—'}</td>
                    <td className="catalogo__linha" data-rotulo="Linha">
                      {linhaDe(p)}
                    </td>
                    <td data-rotulo="Classe">
                      <span
                        className={`etiqueta ${p.classeCode === 'rodape' ? '' : 'etiqueta--acao'}`}
                      >
                        {NOME_CLASSE[p.classeCode] ?? p.classeCode}
                      </span>
                    </td>
                    <td data-rotulo="Unidade">{NOME_UNIDADE[p.unidade] ?? p.unidade}</td>
                    <td className="num" data-rotulo="Por caixa" data-destaque="sim">
                      {decimalBr(p.medidaCaixa)}
                    </td>
                    <td className="num" data-rotulo="Peso/un. (kg)">
                      {decimalBr(p.pesoBrutoUnidade)}
                    </td>
                    <td className="num" data-rotulo="IPI">
                      {decimalBr(String(Number(p.ipi) * 100), 2)}%
                    </td>
                    <td data-rotulo="Situação">
                      {p.ativo ? (
                        <span className="etiqueta etiqueta--ok">Ativo</span>
                      ) : (
                        <span className="etiqueta etiqueta--inativo">Inativo</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <footer className="catalogo__rodape">
            <span className="catalogo__contagem" aria-live="polite">
              {primeiro}–{ultimo} de {total} {total === 1 ? 'produto' : 'produtos'}
              {produtos.isFetching && ' · atualizando…'}
            </span>
            <div className="catalogo__paginacao">
              <button
                type="button"
                className="botao botao--neutro"
                disabled={pagina === 0}
                onClick={() => {
                  setPagina((p) => Math.max(0, p - 1));
                }}
              >
                Anterior
              </button>
              <button
                type="button"
                className="botao botao--neutro"
                disabled={!temProxima}
                onClick={() => {
                  setPagina((p) => p + 1);
                }}
              >
                Próxima
              </button>
            </div>
          </footer>
        </div>
      )}
    </section>
  );
}

/** A "linha" (VECCHIA, CLEAN…) vive em `atributos_json` no backend. */
function linhaDe(p: ProdutoLista): string {
  const linha = p.atributosJson?.['linha'];
  return typeof linha === 'string' && linha !== '' ? linha : '—';
}
